import { fetchWithAuth } from "@/lib/api";
import type { DividendCalendar, EarningsCalendar } from "@/schemas";

import { API_BASE } from "@/config/api";

export interface CalendarAssetMeta {
  asset_id: string;
  symbol?: string;
  name?: string;
  type?: string;
  website?: string;
}

export type CalendarSource = "my-items" | "synced" | "portfolio" | string;

interface CalendarQueryOptions {
  assetIds?: string[];
  source?: CalendarSource;
  watchlistId?: string;
  startDate?: string;
  endDate?: string;
}

interface DividendsCalendarResponse {
  dividends: DividendCalendar[];
  assets: CalendarAssetMeta[];
}

interface EarningsCalendarResponse {
  earnings: EarningsCalendar[];
  assets: CalendarAssetMeta[];
}

async function parseCalendarResponse(response: Response): Promise<Record<string, unknown>> {
  const raw = await response.text();
  if (!raw.trim()) {
    return {};
  }

  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    throw new Error("Received invalid calendar response");
  }
}

function applyCalendarQuery(url: URL, options: CalendarQueryOptions): void {
  if (options.assetIds?.length) {
    url.searchParams.append("asset_ids", options.assetIds.join(","));
  }
  if (options.source) {
    url.searchParams.append("source", options.source);
  }
  if (options.watchlistId) {
    url.searchParams.append("watchlist_id", options.watchlistId);
  }
  if (options.startDate) {
    url.searchParams.append("start_date", options.startDate);
  }
  if (options.endDate) {
    url.searchParams.append("end_date", options.endDate);
  }
}

export class CalendarService {
  static async getDividends(
    options: CalendarQueryOptions = {},
  ): Promise<DividendsCalendarResponse> {
    const url = new URL(`${API_BASE}/financials/calendar/dividends`);
    applyCalendarQuery(url, options);

    const response = await fetchWithAuth(url.toString());
    if (!response.ok) throw new Error("Failed to fetch dividends calendar");
    const data = await parseCalendarResponse(response);
    return {
      dividends: Array.isArray(data.dividends) ? (data.dividends as DividendCalendar[]) : [],
      assets: Array.isArray(data.assets) ? (data.assets as CalendarAssetMeta[]) : [],
    };
  }

  static async getEarnings(
    options: CalendarQueryOptions = {},
  ): Promise<EarningsCalendarResponse> {
    const url = new URL(`${API_BASE}/financials/calendar/earnings`);
    applyCalendarQuery(url, options);

    const response = await fetchWithAuth(url.toString());
    if (!response.ok) throw new Error("Failed to fetch earnings calendar");
    const data = await parseCalendarResponse(response);
    return {
      earnings: Array.isArray(data.earnings) ? (data.earnings as EarningsCalendar[]) : [],
      assets: Array.isArray(data.assets) ? (data.assets as CalendarAssetMeta[]) : [],
    };
  }

  static async getDividendCalendar(
    assetId: string,
  ): Promise<DividendCalendar | null> {
    const url = `${API_BASE}/financials/${assetId}/dividend-calendar`;

    const response = await fetchWithAuth(url);
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error("Failed to fetch dividend calendar");
    }
    return await response.json();
  }

  static async getUpcomingDividends(
    limit: number = 20,
    days: number = 365,
  ): Promise<DividendCalendar[]> {
    const url = new URL(`${API_BASE}/financials/dividends/upcoming`);
    url.searchParams.append("limit", limit.toString());
    url.searchParams.append("days", days.toString());

    const response = await fetchWithAuth(url.toString());
    if (!response.ok) throw new Error("Failed to fetch upcoming dividends");
    const data = await response.json();
    return data.upcoming_dividends || [];
  }
}
